/**
 * Pagination — primitive du design system (S5c).
 *
 * Consomme la forme `Paginated[T]` côté serveur (cf.
 * `backend/app/core/pagination.py`) : `total`, `page`, `page_size`,
 * `has_next`. Affiche un indicateur "Page X / Y" + boutons
 * précédent/suivant. Pas de sélecteur `page_size` pour l'instant —
 * ajout simple si besoin futur, mais YAGNI au lancement S5c.
 *
 *   <Pagination
 *     page={paginated.page}
 *     pageSize={paginated.page_size}
 *     total={paginated.total}
 *     hasNext={paginated.has_next}
 *     onPageChange={(p) => setFilter('page', p)}
 *   />
 *
 * A11y :
 *  - `<nav role="navigation">` avec `aria-label`
 *  - `aria-current="page"` sur le compteur central
 *  - Boutons disabled (pas juste cachés) en bord — la position est stable
 */

import { ChevronLeft, ChevronRight } from 'lucide-react'

import Button from './Button'

interface PaginationProps {
  /** 1-based current page. */
  page: number
  /** Items per page (informationnel — pas modifiable depuis ce composant). */
  pageSize: number
  /** Cardinalité totale (inclus toutes les pages). */
  total: number
  /** Plus de page après celle-ci. Le BE le calcule, on ne le devine pas. */
  hasNext: boolean
  onPageChange: (next: number) => void
  /** Aria label de la `<nav>`. Défaut : "Pagination". */
  ariaLabel?: string
  className?: string
}

export default function Pagination({
  page,
  pageSize,
  total,
  hasNext,
  onPageChange,
  ariaLabel = 'Pagination',
  className,
}: PaginationProps) {
  // Si tout tient sur une page, on ne montre rien — la pagination ne
  // sert à rien quand la grille n'a qu'une page d'items.
  if (total <= pageSize && page === 1) {
    return null
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const hasPrev = page > 1
  // `from` / `to` 1-based inclusifs, pour le label "Showing 1-50 of 183".
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, total)

  const classes = ['ui-pagination', className].filter(Boolean).join(' ')

  return (
    <nav className={classes} role="navigation" aria-label={ariaLabel}>
      <span className="ui-pagination-range" aria-live="polite">
        {from === to ? `${from} sur ${total}` : `${from}–${to} sur ${total}`}
      </span>

      <div className="ui-pagination-controls">
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasPrev}
          onClick={() => onPageChange(Math.max(1, page - 1))}
          aria-label="Page précédente"
        >
          <ChevronLeft size={14} aria-hidden="true" />
          <span>Précédent</span>
        </Button>

        <span
          className="ui-pagination-indicator"
          aria-current="page"
          aria-label={`Page ${page} sur ${totalPages}`}
        >
          {page} / {totalPages}
        </span>

        <Button
          variant="ghost"
          size="sm"
          disabled={!hasNext}
          onClick={() => onPageChange(page + 1)}
          aria-label="Page suivante"
        >
          <span>Suivant</span>
          <ChevronRight size={14} aria-hidden="true" />
        </Button>
      </div>
    </nav>
  )
}
